import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Dialog from '@shared/ui/molecules/Dialog';
import DialogHeader from '@shared/dialogs/DialogHeader';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { UserSearchDropDownList } from '@shared/ui/molecules/UserSearchDropDownList';
import { BasicUserInfo } from '@shared/models/user.models';
import { joinWaitingList, selectIsLockersSaving } from '../data/lockers.duck';

/**
 * Picking a person is the whole interaction, so it gets the screen to itself rather than a card
 * that pushes the queue down the page.
 */
export function AddToWaitingListDialog(props: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isSaving = useSelector(selectIsLockersSaving);

  const onSelect = (user: BasicUserInfo) => {
    dispatch(joinWaitingList({ officeId: props.officeId, userId: user.userId }));
    props.onClose();
  };

  return (
    <Dialog isOpen={true} onClose={props.onClose}>
      <DialogHeader title={'lockers.add-someone-to-list'} />

      <DialogForm>
        <BodySmall colour={Colours.darkGrey}>
          {t('lockers.add-someone-help', { office: props.officeName })}
        </BodySmall>

        <UserSearchDropDownList closeOnSelect={true} disabled={isSaving} portalMenu={true}
                                onSelect={onSelect} />

        <DialogActions>
          <OutlineButton text={'button.cancel'} disabled={isSaving} click={props.onClose} />
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
}

interface Props {
  officeId: number;
  officeName: string;
  onClose: () => void;
}

// Enough room that the dialog does not resize around the results as they appear.
const DialogForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  min-height: 320px;
`

const DialogActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`
